import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { ThemeContext, type Theme } from "./theme-context";

const KEY = "zeaops.theme";

function initialTheme(): Theme {
  try {
    const saved = window.localStorage.getItem(KEY);
    if (saved === "dark" || saved === "light") return saved;
  } catch {
    /* storage unavailable */
  }
  return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(initialTheme);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.add("zh-root");
    root.classList.toggle("zh-dark", theme === "dark");
    root.classList.toggle("zh-light", theme === "light");
    root.style.colorScheme = theme;
    // Phone browsers tint their own bars to match the top of the page.
    let tint = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (!tint) {
      tint = document.createElement("meta");
      tint.name = "theme-color";
      document.head.appendChild(tint);
    }
    tint.content = theme === "dark" ? "#0B0D1F" : "#FFFFFF";
    try {
      window.localStorage.setItem(KEY, theme);
    } catch {
      /* storage unavailable: the theme still applies for this visit */
    }
  }, [theme]);

  const toggle = useCallback(() => setTheme((t) => (t === "dark" ? "light" : "dark")), []);
  const value = useMemo(() => ({ theme, toggle }), [theme, toggle]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
